import { useGet } from './useFetch'
import { useAuth } from './useAuth'
import { ApiResponse, PaginationResponse } from '@magnetar/admin-api-interfaces'

export interface Appointment {
  appointment_id: number
  clinic_id: number
  examination_room_id?: number
  doctor_id?: number
  appointment_setting_id: number
  appointment_date: string
  appointment_time?: string
  sequence_number?: number
  patient_name: string
  patient_health_id?: string
  patient_phone?: string
  patient_email?: string
  appointment_source: 'ONLINE' | 'WALK_IN' | 'PHONE'
  appointment_type: 'SEQUENTIAL' | 'TIME_BASED'
  appointment_status:
    | 'SCHEDULED'
    | 'CHECKED_IN'
    | 'IN_PROGRESS'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'NO_SHOW'
  notes?: string
  metadata?: Record<string, unknown>
  created_at: string
  updated_at: string
  deleted_at?: string
  clinic?: {
    clinic_id: number
    clinic_name: string
  }
  examination_room?: {
    examination_room_id: number
    name: string
    department?: string
  }
  doctor?: {
    doctor_id: number
    name: string
    specialty: string[]
  }
  appointment_setting?: {
    appointment_setting_id: number
    appointment_type: 'SEQUENTIAL' | 'TIME_BASED'
    metadata: Record<string, unknown>
  }
}

interface UseAppointmentsParams {
  page: number
  pageSize: number
  clinicId?: number
  examinationRoomId?: number
  doctorId?: number
  appointmentSettingId?: number
  pendingDispatchOnly?: boolean
  startDate?: string
  endDate?: string
  patientHealthId?: string
  patientName?: string
  appointmentStatus?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export function useAppointments({
  page = 1,
  pageSize = 10,
  clinicId,
  examinationRoomId,
  doctorId,
  appointmentSettingId,
  pendingDispatchOnly,
  startDate,
  endDate,
  patientHealthId,
  patientName,
  appointmentStatus,
  sortBy = 'appointment_date',
  sortOrder = 'asc',
}: UseAppointmentsParams) {
  const { token } = useAuth()

  // 建立查詢參數
  const queryParams: Record<string, unknown> = {
    page,
    pageSize,
    sortBy,
    sortOrder,
  }

  if (clinicId) queryParams.clinicId = clinicId
  if (examinationRoomId) queryParams.examinationRoomId = examinationRoomId
  if (doctorId) queryParams.doctorId = doctorId
  if (appointmentSettingId) queryParams.appointmentSettingId = appointmentSettingId
  if (pendingDispatchOnly) queryParams.pendingDispatchOnly = 'true'
  if (startDate) queryParams.startDate = startDate
  if (endDate) queryParams.endDate = endDate
  if (patientHealthId) queryParams.patientHealthId = patientHealthId
  if (patientName) queryParams.patientName = patientName
  if (appointmentStatus) queryParams.appointmentStatus = appointmentStatus
  queryParams.sortBy = 'sequence_number'
  queryParams.sortOrder = 'asc'

  const queryString = new URLSearchParams(
    Object.entries(queryParams).map(([key, value]) => [key, String(value)]),
  ).toString()
  const url = token ? `/api/appointments?${queryString}` : null

  const { data, error, mutate } = useGet<ApiResponse<PaginationResponse<Appointment>>>(
    url,
    undefined,
    token ?? '',
  )

  return {
    appointments: data?.data?.items,
    pagination: data?.data
      ? {
          page: data.data.page,
          pageSize: data.data.pageSize,
          total: data.data.total,
          totalPages: data.data.totalPages,
        }
      : undefined,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// 根據診間 ID 獲取預約
export function useExaminationRoomAppointments({
  examinationRoomId,
  page = 1,
  pageSize = 10,
  startDate,
  endDate,
}: {
  examinationRoomId: number
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
}) {
  return useAppointments({ examinationRoomId, page, pageSize, startDate, endDate })
}

// 根據預約設定 ID 獲取共用清單（SHARED_ROOMS 模式）
export function useSettingAppointments({
  appointmentSettingId,
  page = 1,
  pageSize = 10,
  startDate,
  endDate,
}: {
  appointmentSettingId?: number
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
}) {
  return useAppointments({ appointmentSettingId, page, pageSize, startDate, endDate })
}

// 根據醫生 ID 獲取預約
export function useDoctorAppointments({
  doctorId,
  page = 1,
  pageSize = 10,
  startDate,
  endDate,
}: {
  doctorId: number
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
}) {
  return useAppointments({ doctorId, page, pageSize, startDate, endDate })
}

// 根據診所 ID 獲取預約
export function useClinicAppointments({
  clinicId,
  page = 1,
  pageSize = 10,
  startDate,
  endDate,
}: {
  clinicId: number
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
}) {
  return useAppointments({ clinicId, page, pageSize, startDate, endDate })
}

// 獲取單個預約詳情
export function useAppointment(appointmentId?: number) {
  const { token } = useAuth()
  const url = token && appointmentId ? `/api/appointments/${appointmentId}` : null

  const { data, error, mutate } = useGet<ApiResponse<Appointment>>(url, undefined, token ?? '')

  return {
    appointment: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}
